import React from "react";
import {
  Shield,
  Database,
  User,
  CreditCard,
  Lock,
  Globe,
  Mail,
  Clock,
  Trash2,
  Cookie,
  FileText,
  AlertCircle,
} from "lucide-react";

const PolitiqueConfidentialitePage = () => {
  return (
    <div className="min-h-screen bg-[#0f172a] text-white">

      {/* HERO */}
      <section className="border-b border-white/10 bg-[#111827]">
        <div className="max-w-5xl mx-auto px-6 py-16">

          <div className="flex items-center gap-3 mb-5">
            <div className="p-3 rounded-xl bg-blue-500/10 text-blue-400">
              <Shield size={28} />
            </div>

            <span className="text-sm font-semibold uppercase tracking-wider text-blue-400">
              Protection des données
            </span>
          </div>

          <h1 className="text-4xl md:text-5xl font-bold mb-5">
            Politique de confidentialité
          </h1>

          <p className="text-gray-400 max-w-3xl text-lg">
            Cette politique explique quelles données personnelles peuvent être
            collectées, pourquoi elles sont utilisées et comment elles sont
            protégées lorsque vous utilisez notre site.
          </p>

          <p className="text-gray-500 text-sm mt-6">
            Dernière mise à jour : octobre 2026
          </p>
        </div>
      </section>

      {/* CONTENT */}
      <main className="max-w-5xl mx-auto px-6 py-14 space-y-12">

        {/* 1 */}
        <section>
          <div className="flex items-center gap-3 mb-5">
            <Shield className="text-blue-400" size={24} />
            <h2 className="text-2xl font-bold">
              1. Responsable du traitement
            </h2>
          </div>

          <div className="bg-white/5 border border-white/10 rounded-2xl p-6 space-y-3 text-gray-300">
            <p>
              Le responsable du traitement des données personnelles collectées
              sur ce site est :
            </p>

            <p>
              <strong className="text-white">
                [NOM DE L'ENTREPRISE]
              </strong>
            </p>

            <p>
              [ADRESSE COMPLÈTE]
            </p>

            <p>
              Suisse
            </p>

            <p>
              E-mail :{" "}
              <a
                href="mailto:[EMAIL]"
                className="text-blue-400 hover:text-blue-300"
              >
                [EMAIL]
              </a>
            </p>
          </div>
        </section>

        {/* 2 */}
        <section>
          <div className="flex items-center gap-3 mb-5">
            <Database className="text-blue-400" size={24} />
            <h2 className="text-2xl font-bold">
              2. Données personnelles collectées
            </h2>
          </div>

          <p className="text-gray-300 leading-7 mb-5">
            Selon votre utilisation du site, nous pouvons collecter différentes
            catégories de données personnelles.
          </p>

          <div className="grid md:grid-cols-2 gap-5">

            <div className="bg-white/5 border border-white/10 rounded-2xl p-6">
              <h3 className="font-semibold text-lg mb-3">
                Données du compte
              </h3>

              <ul className="space-y-2 text-gray-400 list-disc pl-5">
                <li>Nom et prénom</li>
                <li>Adresse e-mail</li>
                <li>Identifiants de compte</li>
                <li>Informations liées au compte</li>
              </ul>
            </div>

            <div className="bg-white/5 border border-white/10 rounded-2xl p-6">
              <h3 className="font-semibold text-lg mb-3">
                Données de commande
              </h3>

              <ul className="space-y-2 text-gray-400 list-disc pl-5">
                <li>Produits achetés</li>
                <li>Date et montant de la commande</li>
                <li>Référence de transaction</li>
                <li>Historique des commandes</li>
              </ul>
            </div>

            <div className="bg-white/5 border border-white/10 rounded-2xl p-6">
              <h3 className="font-semibold text-lg mb-3">
                Données techniques
              </h3>

              <ul className="space-y-2 text-gray-400 list-disc pl-5">
                <li>Adresse IP</li>
                <li>Type d'appareil</li>
                <li>Système d'exploitation</li>
                <li>Navigateur</li>
                <li>Données de connexion</li>
              </ul>
            </div>

            <div className="bg-white/5 border border-white/10 rounded-2xl p-6">
              <h3 className="font-semibold text-lg mb-3">
                Communications
              </h3>

              <ul className="space-y-2 text-gray-400 list-disc pl-5">
                <li>Messages envoyés au support</li>
                <li>Demandes de renseignements</li>
                <li>Correspondances par e-mail</li>
              </ul>
            </div>

          </div>
        </section>

        {/* 3 */}
        <section>
          <div className="flex items-center gap-3 mb-5">
            <User className="text-blue-400" size={24} />
            <h2 className="text-2xl font-bold">
              3. Utilisation des données
            </h2>
          </div>

          <div className="space-y-4 text-gray-300 leading-7">
            <p>
              Les données personnelles peuvent notamment être utilisées pour :
            </p>

            <ul className="list-disc pl-6 space-y-2">
              <li>Créer et gérer votre compte utilisateur ;</li>
              <li>Traiter vos commandes et paiements ;</li>
              <li>Donner accès aux vidéos et produits achetés ;</li>
              <li>Envoyer les confirmations de commande ;</li>
              <li>Répondre aux demandes adressées au support ;</li>
              <li>Assurer la sécurité du site et prévenir les abus ;</li>
              <li>Détecter les activités frauduleuses ;</li>
              <li>Améliorer le fonctionnement et les performances du site ;</li>
              <li>Respecter les obligations légales applicables.</li>
            </ul>
          </div>
        </section>

        {/* 4 */}
        <section>
          <div className="flex items-center gap-3 mb-5">
            <CreditCard className="text-blue-400" size={24} />
            <h2 className="text-2xl font-bold">
              4. Paiements
            </h2>
          </div>

          <div className="space-y-4 text-gray-300 leading-7">
            <p>
              Les paiements peuvent être traités par des prestataires de
              paiement tiers.
            </p>

            <p>
              Lorsque le paiement est effectué auprès d'un prestataire externe,
              les données nécessaires à la réalisation de la transaction
              peuvent être transmises à celui-ci.
            </p>

            <p>
              Les données complètes de carte bancaire ne sont généralement pas
              conservées directement par notre site lorsque le paiement est
              traité par un prestataire spécialisé.
            </p>

            <p>
              Les traitements effectués par ces prestataires peuvent également
              être soumis à leurs propres politiques de confidentialité.
            </p>
          </div>
        </section>

        {/* 5 */}
        <section>
          <div className="flex items-center gap-3 mb-5">
            <Globe className="text-blue-400" size={24} />
            <h2 className="text-2xl font-bold">
              5. Prestataires et transmission des données
            </h2>
          </div>

          <p className="text-gray-300 leading-7 mb-5">
            Pour assurer le fonctionnement du site, certaines données peuvent
            être traitées par des prestataires techniques ou commerciaux.
          </p>

          <div className="space-y-4">

            <div className="bg-white/5 border border-white/10 rounded-2xl p-5">
              <h3 className="font-semibold mb-2">
                Hébergement
              </h3>

              <p className="text-gray-400">
                Les données peuvent être hébergées auprès d'un prestataire
                d'hébergement informatique utilisé pour assurer le
                fonctionnement du site et de ses services.
              </p>
            </div>

            <div className="bg-white/5 border border-white/10 rounded-2xl p-5">
              <h3 className="font-semibold mb-2">
                Paiement
              </h3>

              <p className="text-gray-400">
                Les informations nécessaires aux paiements peuvent être
                transmises à nos prestataires de paiement.
              </p>
            </div>

            <div className="bg-white/5 border border-white/10 rounded-2xl p-5">
              <h3 className="font-semibold mb-2">
                E-mails
              </h3>

              <p className="text-gray-400">
                Des prestataires peuvent être utilisés pour l'envoi des
                e-mails transactionnels, notamment les confirmations de
                commande et les communications liées au compte.
              </p>
            </div>

          </div>
        </section>

        {/* 6 */}
        <section>
          <div className="flex items-center gap-3 mb-5">
            <Globe className="text-blue-400" size={24} />
            <h2 className="text-2xl font-bold">
              6. Transferts de données à l'étranger
            </h2>
          </div>

          <div className="space-y-4 text-gray-300 leading-7">
            <p>
              Certains prestataires utilisés pour le fonctionnement du site
              peuvent être établis en dehors de la Suisse.
            </p>

            <p>
              Lorsque des données personnelles sont transférées à l'étranger,
              nous prenons les mesures requises par la législation applicable
              afin d'assurer un niveau de protection approprié.
            </p>

            <p>
              Selon les services utilisés, les données peuvent notamment être
              traitées dans des pays faisant partie de l'Union européenne ou
              dans d'autres pays.
            </p>
          </div>
        </section>

        {/* 7 */}
        <section>
          <div className="flex items-center gap-3 mb-5">
            <Cookie className="text-blue-400" size={24} />
            <h2 className="text-2xl font-bold">
              7. Cookies et technologies similaires
            </h2>
          </div>

          <div className="space-y-4 text-gray-300 leading-7">
            <p>
              Le site peut utiliser des cookies et technologies similaires afin
              d'assurer son fonctionnement, mémoriser certaines préférences,
              sécuriser les sessions et, lorsque cela est utilisé, mesurer
              l'audience du site.
            </p>

            <p>
              Certains cookies peuvent être nécessaires au fonctionnement
              technique du site, tandis que d'autres peuvent dépendre de vos
              préférences ou des services tiers intégrés au site.
            </p>

            <p>
              Vous pouvez généralement contrôler ou supprimer les cookies à
              partir des paramètres de votre navigateur.
            </p>
          </div>
        </section>

        {/* 8 */}
        <section>
          <div className="flex items-center gap-3 mb-5">
            <Lock className="text-blue-400" size={24} />
            <h2 className="text-2xl font-bold">
              8. Sécurité des données
            </h2>
          </div>

          <div className="space-y-4 text-gray-300 leading-7">
            <p>
              Nous mettons en œuvre des mesures techniques et
              organisationnelles appropriées afin de protéger les données
              personnelles contre les accès non autorisés, la perte,
              l'utilisation abusive, la modification ou la destruction.
            </p>

            <p>
              Ces mesures peuvent notamment comprendre le chiffrement des
              communications, la gestion des accès, la sécurisation des
              comptes et la surveillance des systèmes.
            </p>

            <p>
              Malgré ces mesures, aucune transmission ou conservation de
              données sur Internet ne peut être garantie comme étant totalement
              sécurisée.
            </p>
          </div>
        </section>

        {/* 9 */}
        <section>
          <div className="flex items-center gap-3 mb-5">
            <Clock className="text-blue-400" size={24} />
            <h2 className="text-2xl font-bold">
              9. Durée de conservation
            </h2>
          </div>

          <div className="space-y-4 text-gray-300 leading-7">
            <p>
              Les données personnelles sont conservées pendant la durée
              nécessaire aux finalités pour lesquelles elles ont été
              collectées.
            </p>

            <p>
              Certaines données peuvent être conservées plus longtemps lorsque
              cela est nécessaire pour respecter des obligations légales,
              comptables, fiscales ou pour permettre la constatation, l'exercice
              ou la défense de droits en justice.
            </p>

            <p>
              Lorsque les données ne sont plus nécessaires et qu'aucune
              obligation de conservation ne s'y oppose, elles sont supprimées
              ou anonymisées.
            </p>
          </div>
        </section>

        {/* 10 */}
        <section>
          <div className="flex items-center gap-3 mb-5">
            <Trash2 className="text-blue-400" size={24} />
            <h2 className="text-2xl font-bold">
              10. Suppression du compte
            </h2>
          </div>

          <div className="space-y-4 text-gray-300 leading-7">
            <p>
              Vous pouvez demander la suppression de votre compte en contactant
              le responsable du traitement à l'adresse indiquée dans la
              présente politique.
            </p>

            <p>
              La suppression du compte peut entraîner la suppression des
              données qui y sont associées, sous réserve des données devant
              être conservées pour des raisons légales ou légitimes.
            </p>
          </div>
        </section>

        {/* 11 */}
        <section>
          <div className="flex items-center gap-3 mb-5">
            <FileText className="text-blue-400" size={24} />
            <h2 className="text-2xl font-bold">
              11. Vos droits
            </h2>
          </div>

          <div className="space-y-4 text-gray-300 leading-7">
            <p>
              Conformément à la législation applicable en matière de protection
              des données, vous disposez notamment de droits concernant vos
              données personnelles.
            </p>

            <ul className="list-disc pl-6 space-y-2">
              <li>
                demander si des données personnelles vous concernant sont
                traitées ;
              </li>

              <li>
                demander l'accès aux données personnelles vous concernant ;
              </li>

              <li>
                demander la rectification de données incorrectes ou incomplètes ;
              </li>

              <li>
                demander, lorsque les conditions légales sont réunies, la
                suppression de certaines données ;
              </li>

              <li>
                vous opposer à certains traitements lorsque la législation
                applicable le permet ;
              </li>

              <li>
                demander des informations sur les traitements effectués.
              </li>
            </ul>

            <p>
              Pour exercer vos droits, vous pouvez nous contacter à :
            </p>

            <p>
              <a
                href="mailto:[EMAIL]"
                className="text-blue-400 hover:text-blue-300"
              >
                [EMAIL]
              </a>
            </p>
          </div>
        </section>

        {/* 12 */}
        <section>
          <div className="flex items-center gap-3 mb-5">
            <Mail className="text-blue-400" size={24} />
            <h2 className="text-2xl font-bold">
              12. Communications commerciales
            </h2>
          </div>

          <div className="space-y-4 text-gray-300 leading-7">
            <p>
              Lorsque des communications commerciales sont envoyées, elles
              respectent les exigences légales applicables.
            </p>

            <p>
              Lorsque la loi ou les circonstances l'exigent, vous pouvez
              refuser ou vous désinscrire des communications commerciales en
              utilisant le mécanisme de désinscription prévu dans les
              communications concernées.
            </p>

            <p>
              Les communications nécessaires à l'exécution d'une commande ou à
              la gestion d'un compte, telles que les confirmations de commande
              ou les informations importantes relatives au service, peuvent
              continuer à être envoyées lorsqu'elles sont nécessaires à la
              relation contractuelle.
            </p>
          </div>
        </section>

        {/* 13 */}
        <section>
          <div className="flex items-center gap-3 mb-5">
            <AlertCircle className="text-blue-400" size={24} />
            <h2 className="text-2xl font-bold">
              13. Violations de données
            </h2>
          </div>

          <p className="text-gray-300 leading-7">
            En cas de violation de la sécurité des données présentant un risque
            élevé pour la personnalité ou les droits fondamentaux des personnes
            concernées, les mesures et notifications prévues par la législation
            applicable seront mises en œuvre.
          </p>
        </section>

        {/* 14 */}
        <section>
          <div className="flex items-center gap-3 mb-5">
            <FileText className="text-blue-400" size={24} />
            <h2 className="text-2xl font-bold">
              14. Modifications de la politique
            </h2>
          </div>

          <div className="space-y-4 text-gray-300 leading-7">
            <p>
              Cette politique de confidentialité peut être modifiée afin de
              tenir compte de l'évolution du site, des services proposés, des
              technologies utilisées ou de la législation applicable.
            </p>

            <p>
              La version publiée sur le site est la version applicable à
              compter de sa date de mise à jour.
            </p>
          </div>
        </section>

        {/* 15 */}
        <section className="border-t border-white/10 pt-10">
          <div className="bg-blue-500/5 border border-blue-500/20 rounded-2xl p-6">
            <h2 className="text-xl font-bold mb-3">
              Contact relatif à la protection des données
            </h2>

            <p className="text-gray-300 mb-4">
              Pour toute question concernant vos données personnelles ou
              l'exercice de vos droits :
            </p>

            <a
              href="mailto:[EMAIL]"
              className="text-blue-400 hover:text-blue-300 font-medium"
            >
              [EMAIL]
            </a>
          </div>
        </section>

      </main>
    </div>
  );
};

export default PolitiqueConfidentialitePage;